import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Phone, ArrowLeft, ArrowRight, Check, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { companyInfo, formOptions } from '../../data/mock';
import { toast } from 'sonner';

const STEPS = [
  { id: 1, title: 'Coordonnées', description: 'Vos informations de contact' },
  { id: 2, title: 'Demande', description: 'Type d\'intervention' },
  { id: 3, title: 'Détails', description: 'Description du problème' },
  { id: 4, title: 'Accès', description: 'Informations logement' },
  { id: 5, title: 'Validation', description: 'Récapitulatif' }
];

const ContactPage = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    postalCode: '',
    city: '',
    requestType: '',
    serviceType: '',
    description: '',
    isUrgent: false,
    preferredDate: '',
    housingType: '',
    accessType: '',
    isPresent: true,
    consent: false
  });

  const [errors, setErrors] = useState({});

  const searchAddress = useCallback(async (query) => {
    if (query.length < 3) {
      setAddressSuggestions([]);
      return;
    }
    try {
      const response = await fetch(
        `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(query)}&limit=5`
      );
      const data = await response.json();
      setAddressSuggestions(data.features || []);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Address search error:', error);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.address) {
        searchAddress(formData.address);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [formData.address, searchAddress]);

  const selectAddress = (suggestion) => {
    const props = suggestion.properties;
    setFormData(prev => ({
      ...prev,
      address: props.name || props.label,
      postalCode: props.postcode || '',
      city: props.city || ''
    }));
    setShowSuggestions(false);
    setAddressSuggestions([]);
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    switch (step) {
      case 1:
        if (!formData.name.trim()) newErrors.name = 'Nom requis';
        if (!formData.phone.trim()) newErrors.phone = 'Téléphone requis';
        else if (!/^[0-9\s+]{10,}$/.test(formData.phone.replace(/\s/g, ''))) 
          newErrors.phone = 'Numéro invalide';
        if (!formData.email.trim()) newErrors.email = 'Email requis';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) 
          newErrors.email = 'Email invalide';
        break;
      case 2:
        if (!formData.requestType) newErrors.requestType = 'Type de demande requis';
        if (!formData.serviceType) newErrors.serviceType = 'Service requis';
        break;
      case 3:
        if (!formData.description.trim()) newErrors.description = 'Description requise';
        break;
      case 4:
        if (!formData.housingType) newErrors.housingType = 'Type de logement requis';
        if (!formData.accessType) newErrors.accessType = 'Type d\'accès requis';
        break;
      case 5:
        if (!formData.consent) newErrors.consent = 'Consentement requis';
        break;
      default:
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 5));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(5)) return;
    
    setIsSubmitting(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          address: formData.address || null,
          postalCode: formData.postalCode || null,
          city: formData.city || null,
          requestType: formData.requestType,
          serviceType: formData.serviceType,
          description: formData.description,
          isUrgent: formData.isUrgent,
          preferredDate: formData.preferredDate || null,
          housingType: formData.housingType,
          accessType: formData.accessType,
          isPresent: formData.isPresent,
          consent: formData.consent
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Erreur serveur');
      }
      
      setIsSubmitted(true);
      toast.success('Demande envoyée avec succès !');
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de l\'envoi. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="py-16 md:py-24">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-[#262626] mb-4">Demande envoyée !</h1>
          <p className="text-lg text-[#666666] mb-8">
            Merci pour votre demande. Nous vous recontacterons dans les plus brefs délais.
          </p>
          <div className="bg-[#B91C1C]/10 rounded-lg p-6 mb-8">
            <p className="text-[#262626] font-medium mb-2">Besoin d'une intervention urgente à Vitry-sur-Seine ?</p>
            <a 
              href={`tel:${companyInfo.phoneEmergency.replace(/\s/g, '')}`}
              className="text-2xl font-bold text-[#B91C1C]"
            >
              {companyInfo.phoneEmergency}
            </a>
          </div>
          <Link to="/">
            <Button variant="outline" className="border-[#B91C1C] text-[#B91C1C]">Retour à l'accueil</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="py-12 md:py-16">
      <div className="max-w-4xl mx-auto px-4">
        <nav className="mb-8 text-sm">
          <ol className="flex items-center gap-2 text-[#666666]">
            <li><Link to="/" className="hover:text-[#B91C1C]">Accueil</Link></li>
            <li>/</li>
            <li className="text-[#262626] font-medium">Demander un devis</li>
          </ol>
        </nav>

        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-[#262626] mb-2">
            Demander un devis gratuit à Vitry-sur-Seine
          </h1>
          <p className="text-[#666666]">
            Remplissez le formulaire ci-dessous, nous vous recontactons rapidement
          </p>
        </div>

        {/* Progress Steps - ROUGE */}
        <div className="mb-8">
          <div className="flex items-center justify-between relative">
            {STEPS.map((step) => (
              <div key={step.id} className="flex flex-col items-center relative z-10">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors ${
                  currentStep > step.id 
                    ? 'bg-[#B91C1C] text-white' 
                    : currentStep === step.id 
                      ? 'bg-[#B91C1C] text-white' 
                      : 'bg-gray-200 text-[#666666]'
                }`}>
                  {currentStep > step.id ? <Check className="w-5 h-5" /> : step.id}
                </div>
                <span className={`text-xs mt-2 hidden sm:block ${
                  currentStep >= step.id ? 'text-[#262626]' : 'text-[#666666]'
                }`}>
                  {step.title}
                </span>
              </div>
            ))}
            <div className="absolute top-5 left-0 right-0 h-0.5 bg-gray-200 -z-10">
              <div 
                className="h-full bg-[#B91C1C] transition-all duration-300"
                style={{ width: `${((currentStep - 1) / (STEPS.length - 1)) * 100}%` }}
              />
            </div>
          </div>
        </div>

        <Card>
          <CardContent className="p-6 md:p-8">
            {/* Step 1 */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold text-[#262626] mb-4">Vos coordonnées</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Nom complet *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => updateFormData('name', e.target.value)}
                      placeholder="Jean Dupont"
                      className={errors.name ? 'border-red-500' : ''}
                    />
                    {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                  </div>
                  <div>
                    <Label htmlFor="phone">Téléphone *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => updateFormData('phone', e.target.value)}
                      placeholder="06 12 34 56 78"
                      className={errors.phone ? 'border-red-500' : ''}
                    />
                    {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                  </div>
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => updateFormData('email', e.target.value)}
                    placeholder="jean.dupont@email.com"
                    className={errors.email ? 'border-red-500' : ''}
                  />
                  {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                </div>
                <div className="relative">
                  <Label htmlFor="address">Adresse d'intervention</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => updateFormData('address', e.target.value)}
                    placeholder="Commencez à taper votre adresse..."
                    autoComplete="off"
                  />
                  {showSuggestions && addressSuggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-auto">
                      {addressSuggestions.map((suggestion, idx) => (
                        <button
                          key={idx}
                          type="button"
                          className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b last:border-b-0"
                          onClick={() => selectAddress(suggestion)}
                        >
                          <p className="text-[#262626]">{suggestion.properties.label}</p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="postalCode">Code postal</Label>
                    <Input
                      id="postalCode"
                      value={formData.postalCode}
                      onChange={(e) => updateFormData('postalCode', e.target.value)}
                      placeholder="94400"
                    />
                  </div>
                  <div>
                    <Label htmlFor="city">Ville</Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) => updateFormData('city', e.target.value)}
                      placeholder="Vitry-sur-Seine"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 2 */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold text-[#262626] mb-4">Votre demande</h2>
                <div>
                  <Label>Type de demande *</Label>
                  <RadioGroup
                    value={formData.requestType}
                    onValueChange={(value) => updateFormData('requestType', value)}
                    className="mt-2"
                  >
                    {formOptions.requestTypes.map((type) => (
                      <div key={type.value} className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50">
                        <RadioGroupItem value={type.value} id={type.value} />
                        <Label htmlFor={type.value} className="cursor-pointer flex-1">
                          {type.label}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                  {errors.requestType && <p className="text-red-500 text-sm mt-1">{errors.requestType}</p>}
                </div>
                <div>
                  <Label>Service concerné *</Label>
                  <Select
                    value={formData.serviceType}
                    onValueChange={(value) => updateFormData('serviceType', value)}
                  >
                    <SelectTrigger className={errors.serviceType ? 'border-red-500' : ''}>
                      <SelectValue placeholder="Sélectionnez un service" />
                    </SelectTrigger>
                    <SelectContent>
                      {formOptions.serviceTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.serviceType && <p className="text-red-500 text-sm mt-1">{errors.serviceType}</p>}
                </div>
              </div>
            )}

            {/* Step 3 */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold text-[#262626] mb-4">Détails de votre demande</h2>
                <div>
                  <Label htmlFor="description">Description du problème ou de la demande *</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => updateFormData('description', e.target.value)}
                    placeholder="Décrivez votre situation en détail (type de vitre, serrure, dimensions approximatives, etc.)"
                    rows={5}
                    className={errors.description ? 'border-red-500' : ''}
                  />
                  {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description}</p>}
                </div>
                <div className="flex items-start gap-3 p-4 bg-[#B91C1C]/10 rounded-lg">
                  <Checkbox
                    id="isUrgent"
                    checked={formData.isUrgent}
                    onCheckedChange={(checked) => updateFormData('isUrgent', checked)}
                  />
                  <div>
                    <Label htmlFor="isUrgent" className="cursor-pointer font-semibold text-[#B91C1C]">
                      Intervention urgente
                    </Label>
                    <p className="text-sm text-[#666666]">
                      Cochez si vous avez besoin d'une intervention dans les plus brefs délais
                    </p>
                  </div>
                </div>
                <div>
                  <Label htmlFor="preferredDate">Date souhaitée (si intervention programmée)</Label>
                  <Input
                    id="preferredDate"
                    type="date"
                    value={formData.preferredDate}
                    onChange={(e) => updateFormData('preferredDate', e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
              </div>
            )}

            {/* Step 4 */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold text-[#262626] mb-4">Informations sur le logement</h2>
                <div>
                  <Label>Type de logement *</Label>
                  <Select
                    value={formData.housingType}
                    onValueChange={(value) => updateFormData('housingType', value)}
                  >
                    <SelectTrigger className={errors.housingType ? 'border-red-500' : ''}>
                      <SelectValue placeholder="Sélectionnez" />
                    </SelectTrigger>
                    <SelectContent>
                      {formOptions.housingTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.housingType && <p className="text-red-500 text-sm mt-1">{errors.housingType}</p>}
                </div>
                <div>
                  <Label>Facilité d'accès *</Label>
                  <Select
                    value={formData.accessType}
                    onValueChange={(value) => updateFormData('accessType', value)}
                  >
                    <SelectTrigger className={errors.accessType ? 'border-red-500' : ''}>
                      <SelectValue placeholder="Sélectionnez" />
                    </SelectTrigger>
                    <SelectContent>
                      {formOptions.accessTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.accessType && <p className="text-red-500 text-sm mt-1">{errors.accessType}</p>}
                </div>
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="isPresent"
                    checked={formData.isPresent}
                    onCheckedChange={(checked) => updateFormData('isPresent', checked)}
                  />
                  <Label htmlFor="isPresent" className="cursor-pointer">
                    Je serai présent lors de l'intervention
                  </Label>
                </div>
              </div>
            )}

            {/* Step 5 */}
            {currentStep === 5 && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold text-[#262626] mb-4">Récapitulatif</h2>
                <div className="bg-gray-50 rounded-lg p-6 space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-[#666666]">Nom</p>
                      <p className="font-medium text-[#262626]">{formData.name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-[#666666]">Téléphone</p>
                      <p className="font-medium text-[#262626]">{formData.phone}</p>
                    </div>
                    <div>
                      <p className="text-sm text-[#666666]">Email</p>
                      <p className="font-medium text-[#262626]">{formData.email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-[#666666]">Adresse</p>
                      <p className="font-medium text-[#262626]">
                        {formData.address || '-'} {formData.postalCode} {formData.city}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-[#666666]">Type de demande</p>
                      <p className="font-medium text-[#262626]">
                        {formOptions.requestTypes.find(t => t.value === formData.requestType)?.label || '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-[#666666]">Service</p>
                      <p className="font-medium text-[#262626]">
                        {formOptions.serviceTypes.find(t => t.value === formData.serviceType)?.label || '-'}
                      </p>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-[#666666]">Description</p>
                    <p className="font-medium text-[#262626]">{formData.description}</p>
                  </div>
                  {formData.isUrgent && (
                    <div className="bg-[#B91C1C]/10 text-[#B91C1C] px-3 py-2 rounded font-medium inline-block">
                      Intervention urgente demandée
                    </div>
                  )}
                </div>
                <div className="flex items-start gap-3 p-4 border rounded-lg">
                  <Checkbox
                    id="consent"
                    checked={formData.consent}
                    onCheckedChange={(checked) => updateFormData('consent', checked)}
                  />
                  <div>
                    <Label htmlFor="consent" className="cursor-pointer">
                      J'accepte que mes données soient utilisées pour traiter ma demande *
                    </Label>
                    <p className="text-sm text-[#666666]">
                      Vos données sont uniquement utilisées pour répondre à votre demande.
                    </p>
                  </div>
                </div>
                {errors.consent && <p className="text-red-500 text-sm">{errors.consent}</p>}
              </div>
            )}

            {/* Navigation */}
            <div className="flex justify-between mt-8 pt-6 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={prevStep}
                disabled={currentStep === 1}
                className={currentStep === 1 ? 'invisible' : ''}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Précédent
              </Button>
              {currentStep < 5 ? (
                <Button type="button" onClick={nextStep} className="bg-[#B91C1C] hover:bg-[#991B1B]">
                  Suivant
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button 
                  type="button" 
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="bg-[#B91C1C] hover:bg-[#991B1B]"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Envoi...
                    </>
                  ) : (
                    'Envoyer ma demande'
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="mt-8 text-center">
          <p className="text-[#666666] mb-2">Besoin d'une réponse immédiate ?</p>
          <a 
            href={`tel:${companyInfo.phoneEmergency.replace(/\s/g, '')}`}
            className="inline-flex items-center gap-2 text-[#B91C1C] font-bold text-lg hover:underline"
          >
            <Phone className="w-5 h-5" />
            {companyInfo.phoneEmergency}
          </a>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;